import { z } from "zod";
import {
  ADS_CALL_TO_ACTION_SEND_MESSAGE_PAGE_META_VALUE,
  ADS_CAMPAIGN_GET_DIRECTIONS_CALL_TO_ACTION_OPTION,
  ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES,
  ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_PARTNER_FIRST,
  ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_THRUPLAY,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH,
  ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH,
  MESSAGE_TEMPLATE_TYPE_UNSUPPORTED,
  VIDEO_FORMATS,
} from "../../../../../utils/constants";
import {
  FIELD_MAX_LENGTH,
  FIELD_MAX_LENGTH_SELECT,
  FIELD_MIN_LENGTH_SELECT,
  FIELD_REQUIRED,
  FIELD_TEXT_REPEATED,
  FIELD_URL_VALID,
  FIELD_CREATIVE_MUST_BE_VIDEO_NOT_IMAGE,
  FIELDS_REQUIRED,
  META,
} from "../../../../../i18n/keysTranslations";
import i18next from "i18next";
import {
  getIsSpecialAdWithOnlyTitles,
  getIsSpecialAdWithoutCallToAction,
  getShowDestinationTypeAd,
} from "../../../../../services/adsCampaigns";
import { REGEX_URL } from "../../../../../utils/regex";
import {
  CARD_TYPES,
  getIsMessageTemplateRequired,
  getIsPostCarousel,
} from "../../../../../services/meta";
import { getExtensionFile } from "../../../../../utils/string";
import _ from "lodash";

function getIndicesMap(arrayOfStrings) {
  const map = {};
  arrayOfStrings.forEach((txt, i) => {
    if (!map[txt]) {
      map[txt] = [];
    }
    map[txt].push(i);
  });
  return map;
}

const hasCatalogCarouselCard = (mediaData = []) =>
  mediaData.some((item) => item.type === CARD_TYPES.CATALOG);

const addCatalogCarouselIntroMediaUrlIssueIfMissing = (
  data,
  ctx,
  basicPaths
) => {
  const introIndex = (data?.mediaData || []).findIndex(
    (item) => item.type === CARD_TYPES.INTRO
  );
  if (introIndex === -1) return;

  const mediaUrlIntro = data?.mediaUrls?.[introIndex];
  if (!mediaUrlIntro?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18next.t(FIELD_REQUIRED),
      path: [...basicPaths, "mediaUrls", introIndex],
    });
  }
};

export const adMetaSchema = ({ campaign, adGroup, indexAdGroup, indexAd }) =>
  z
    .object({
      id: z.string().optional(),
      platform: z.literal(META),
      isCarousel: z.any().optional(),
      isInstantExperience: z.any().optional(),
      instantExperience: z.any().optional(),
      callToAction: z.any().optional(),
      callToActions: z.any().optional(),
      destinationType: z.any().optional(),
      mediaUrls: z.array(z.any().optional()).optional(),
      mediaData: z.array(z.any().optional()).optional(),

      destinationUrls: z.any().optional(),
      titles: z.any().optional(),
      descriptions: z.any().optional(),
      headlines: z.any().optional(),

      messageTemplate: z.any().optional(),
      instantForm: z.any().optional(),

      creativeSource: z.any().optional(),

      address: z.any().optional(),

      productSetID: z.any().optional(),

      isPostAd: z.boolean().optional(),
      reuseExistingCreative: z.boolean().optional(),
      adSetupMode: z.string().optional(),
      creativeID: z.string().optional(),

      isPartnershipAd: z.boolean().optional(),
      partnershipOrderType: z.string().optional(),
      sponsorPageID: z.string().optional(),
      sponsorInstagramID: z.string().optional(),

      // Add date fields
      startDate: z.any().optional(),
      endDate: z.any().optional(),
    })

    .superRefine((data, ctx) => {
      const basicPaths = ["adsGroups", indexAdGroup, "ads", indexAd];

      const objective = campaign.platformsData?.[META]?.objective;
      const campaignPageID = campaign.platformsData?.[META]?.pageID;
      const isCatalogCampaign =
        campaign.platformsData?.[META]?.isCatalogCampaign;
      const isAppPromotionCampaign =
        campaign.platformsData?.[META]?.objective ===
        ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION;
      const destinationTypeAdGroup = adGroup.destinationType;
      const isDestinationTypeWebsiteAdGroup =
        destinationTypeAdGroup ===
        ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION;
      const catalogIDAdGroup = adGroup.catalogID;

      const isCatalogAd =
        isCatalogCampaign ||
        (isAppPromotionCampaign && catalogIDAdGroup) ||
        data.creativeSource ===
          ADS_CAMPAIGN_META_CREATIVE_SOURCE_TYPES.ADVANTAGE_CATALOG;

      const destinationTypeAd = data.destinationType;

      const showDestinationTypeAd = getShowDestinationTypeAd({
        objective,
        destinationTypeAdGroup,
      });

      const isDynamicCreative = adGroup.isDynamicCreative === true;
      const showPartnershipAd =
        destinationTypeAdGroup !==
          ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION &&
        data.isPostAd !== true;

      if (data.isPartnershipAd && showPartnershipAd) {
        const partnershipOrderType = String(
          data.partnershipOrderType ?? ""
        ).trim();
        if (
          !partnershipOrderType ||
          (partnershipOrderType !==
            ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST &&
            partnershipOrderType !==
              ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_PARTNER_FIRST)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "partnershipOrderType"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (!data.sponsorPageID?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "sponsorPageID"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (
          campaignPageID &&
          data.sponsorPageID &&
          String(data.sponsorPageID) === String(campaignPageID)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "sponsorPageID"],
            message: i18next.t(FIELD_TEXT_REPEATED),
          });
        }
      }

      const isRequiredCallToAction = !getIsSpecialAdWithoutCallToAction({
        platform: META,
        objective,
        destinationTypeAdGroup,
        destinationTypeAd,
        mediaData: data.mediaData,
        callToAction: isDynamicCreative
          ? data.callToActions?.[0]
          : data.callToAction,
      });

      const isDirectionMapCallToAction =
        objective === ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION &&
        (isDynamicCreative ? data.callToActions?.[0] : data.callToAction) ===
          ADS_CAMPAIGN_GET_DIRECTIONS_CALL_TO_ACTION_OPTION;

      if (isDynamicCreative) {
        if (isRequiredCallToAction) {
          const callToActions = data.callToActions || [];
          if (callToActions.length < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "callToActions"],
              message: i18next.t(FIELD_MIN_LENGTH_SELECT, { value: 1 }),
            });
          } else if (callToActions.length > 5) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "callToActions"],
              message: i18next.t(FIELD_MAX_LENGTH_SELECT, { value: 5 }),
            });
          }
          callToActions.forEach((callToAction, index) => {
            if (index > 5) {
              return;
            }
            if (!callToAction) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [...basicPaths, "callToActions", index],
                message: i18next.t(FIELD_REQUIRED),
              });
            }
          });
        }
      } else if (!data.callToAction && isRequiredCallToAction) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "callToAction"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      if (isDirectionMapCallToAction && !data.address) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "address"],
          message: i18next.t(FIELD_REQUIRED),
        });
      } else if (isDirectionMapCallToAction && data.address) {
        if (typeof data.address.lat !== "number" || isNaN(data.address.lat)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "address"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }

        if (typeof data.address.lng !== "number" || isNaN(data.address.lng)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "address"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }

        if (
          !data.address.name ||
          typeof data.address.name !== "string" ||
          data.address.name.trim() === ""
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "address"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
      }

      const isDestinationTypeAdWebsite =
        !showDestinationTypeAd ||
        (showDestinationTypeAd &&
          destinationTypeAd ===
            ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION);

      const isCarousel = data.isCarousel;
      const isInstantExperience = data.isInstantExperience;

      const isThruPlayOptimization =
        adGroup.optimizationGoal ===
        ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_THRUPLAY;

      if (isThruPlayOptimization) {
        if (isCarousel) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [...basicPaths, "isCarousel"],
            message: i18next.t(FIELD_CREATIVE_MUST_BE_VIDEO_NOT_IMAGE),
          });
        }

        (data.mediaUrls || []).forEach((mediaUrl, index) => {
          if (!mediaUrl?.trim()) return;
          const extension = getExtensionFile(mediaUrl);
          if (!VIDEO_FORMATS.includes(extension)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [...basicPaths, "mediaUrls", index],
              message: i18next.t(FIELD_CREATIVE_MUST_BE_VIDEO_NOT_IMAGE),
            });
          }
        });
      }

      const isPostCarousel = getIsPostCarousel({
        objective,
        destinationType: destinationTypeAdGroup,
        mediaData: data.mediaData,
        isPostAd: data.isPostAd,
      });

      const isPostAdMode =
        data.isPostAd ||
        destinationTypeAdGroup ===
          ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION;
      const isSpecialAdWithOnlyTitles = getIsSpecialAdWithOnlyTitles({
        platform: META,
        objective,
        destinationType: destinationTypeAdGroup,
      });
      // Skip leftover copy that the current setup hides (post ads, title-only
      // destinations, or a non-website ad destination on the single-ad form).
      // Carousel and dynamic still show headlines/descriptions.
      const shouldValidateHeadlinesAndDescriptions =
        !isPostAdMode &&
        (isCarousel ||
          isDynamicCreative ||
          (!isSpecialAdWithOnlyTitles && isDestinationTypeAdWebsite));
      const shouldValidateTitles = !(
        isDynamicCreative && isSpecialAdWithOnlyTitles
      );
      if (isPostAdMode && !data.mediaData?.[0]?.postID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "mediaUrls", 0],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      if (data.reuseExistingCreative && !data.creativeID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "creativeID"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      const isMessageTemplateRequired = getIsMessageTemplateRequired({
        objective,
        mediaData: data.mediaData,
        callToAction: isDynamicCreative
          ? data.callToActions?.[0]
          : data.callToAction,
        destinationType: destinationTypeAdGroup,
        isPostAd: data.isPostAd,
      });

      if (isMessageTemplateRequired) {
        const mt = data.messageTemplate;
        const messageTemplatePath = [...basicPaths, "messageTemplate"];

        if (!mt || typeof mt !== "object") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: messageTemplatePath,
            message: i18next.t(FIELDS_REQUIRED),
          });
        } else if (
          mt?.customerTypeMessage === MESSAGE_TEMPLATE_TYPE_UNSUPPORTED
        ) {
          // Template created outside the platform (WhatsApp Forms / Flows).
          // It is forwarded to Meta untouched, so there is nothing to validate.
        } else {
          const greeting = mt?.greeting;
          const customerTypeMessage = mt?.customerTypeMessage;
          const customerTexts = mt?.customerTexts;

          const isGreetingMissing =
            greeting == null ||
            typeof greeting !== "string" ||
            greeting.trim() === "";
          const isGreetingTooLong =
            typeof greeting === "string" && greeting.length > 200;

          const isCustomerTypeMissing =
            !customerTypeMessage ||
            !["preFill", "quickQuestions"].includes(customerTypeMessage);

          const isCustomerTextsMissing =
            !Array.isArray(customerTexts) || customerTexts.length === 0;
          const hasEmptyCustomerText = Array.isArray(customerTexts)
            ? customerTexts.some(
                (txt) =>
                  txt == null || typeof txt !== "string" || txt.trim() === ""
              )
            : false;
          const hasCustomerTextTooLong = Array.isArray(customerTexts)
            ? customerTexts.some(
                (txt) => typeof txt === "string" && txt.length > 80
              )
            : false;

          const isPreFill = customerTypeMessage === "preFill";
          const hasTooManyCustomerTexts =
            isPreFill &&
            Array.isArray(customerTexts) &&
            customerTexts.length > 1;
          const hasTooManyQuickQuestions =
            !isPreFill &&
            Array.isArray(customerTexts) &&
            customerTexts.length > 5;

          if (
            isGreetingMissing ||
            isGreetingTooLong ||
            isCustomerTypeMissing ||
            isCustomerTextsMissing ||
            hasEmptyCustomerText ||
            hasCustomerTextTooLong ||
            hasTooManyCustomerTexts ||
            hasTooManyQuickQuestions
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: messageTemplatePath,
              message: i18next.t(FIELDS_REQUIRED),
            });
          }
        }
      }

      const effectiveCallToAction = isDynamicCreative
        ? data.callToActions?.[0]
        : data.callToAction;
      if (
        isPostCarousel &&
        effectiveCallToAction !==
          ADS_CALL_TO_ACTION_SEND_MESSAGE_PAGE_META_VALUE &&
        !data.destinationUrls?.[0]?.value?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "destinationUrls", 0, "value"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      if (
        objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION &&
        destinationTypeAdGroup ===
          ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_INSTANT_FORM_OPTION &&
        !data.instantForm
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "instantForm"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      if (
        (destinationTypeAdGroup ===
          ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION ||
          data.isPostAd === true) &&
        !data.mediaData?.[0]?.postID
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...basicPaths, "mediaData", 0],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      if (
        !data.reuseExistingCreative &&
        isDestinationTypeWebsiteAdGroup &&
        isDestinationTypeAdWebsite &&
        !data.isInstantExperience
      ) {
        if (isCarousel && !isDynamicCreative) {
          const expectedLength = data.mediaUrls.length;

          const destinationUrls =
            data.destinationUrls?.slice(0, expectedLength) || [];

          destinationUrls.forEach((destinationUrl, index) => {
            if (
              !destinationUrl?.value ||
              destinationUrl?.value?.trim() === ""
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18next.t(FIELD_REQUIRED),
                path: [...basicPaths, "destinationUrls", index, "value"],
              });
            }

            if (!REGEX_URL.test(encodeURI(destinationUrl.value))) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18next.t(FIELD_URL_VALID),
                path: [...basicPaths, "destinationUrls", index, "value"],
              });
            }
          });
        } else {
          if (
            !data?.destinationUrls?.length ||
            !data?.destinationUrls?.[0]?.value?.trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18next.t(FIELD_REQUIRED),
              path: [...basicPaths, "destinationUrls", 0, "value"],
            });
          }
          if (!REGEX_URL.test(encodeURI(data?.destinationUrls?.[0]?.value))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18next.t(FIELD_URL_VALID),
              path: [...basicPaths, "destinationUrls", 0, "value"],
            });
          }
        }
      }

      if (!isCatalogAd && !data.reuseExistingCreative) {
        if (shouldValidateTitles) {
          const titlesNormalized = data.titles
            .filter((item) => item?.value)
            .map((item) => item.value.trim().toLowerCase())
            .filter(Boolean);
          const titlesMap = getIndicesMap(titlesNormalized);

          Object.entries(titlesMap).forEach(([, indexes]) => {
            if (indexes.length > 1) {
              indexes.forEach((i) => {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [...basicPaths, "titles", i, "value"],
                  message: i18next.t(FIELD_TEXT_REPEATED),
                });
              });
            }
          });
        }

        if (
          shouldValidateHeadlinesAndDescriptions &&
          (!isCarousel || isDynamicCreative) &&
          isDestinationTypeAdWebsite
        ) {
          const headlinesNormalized = (data.headlines || [])
            .filter((item) => item?.value)
            .map((item) => item.value.trim().toLowerCase())
            .filter(Boolean);
          const headlinesMap = getIndicesMap(headlinesNormalized);

          Object.entries(headlinesMap).forEach(([, indexes]) => {
            if (indexes.length > 1) {
              indexes.forEach((i) => {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [...basicPaths, "headlines", i, "value"],
                  message: i18next.t(FIELD_TEXT_REPEATED),
                });
              });
            }
          });
          const descriptionsNormalized = (data.descriptions || [])
            .filter((item) => item?.value)
            .map((item) => item.value.trim().toLowerCase())
            .filter(Boolean);
          const descriptionsMap = getIndicesMap(descriptionsNormalized);

          Object.entries(descriptionsMap).forEach(([, indexes]) => {
            if (indexes.length > 1) {
              indexes.forEach((i) => {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [...basicPaths, "descriptions", i, "value"],
                  message: i18next.t(FIELD_TEXT_REPEATED),
                });
              });
            }
          });
        }

        if (isDynamicCreative) {
          const mediaUrls = (data.mediaUrls || []).filter((url) => url?.trim());
          if (!mediaUrls?.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18next.t(FIELD_REQUIRED),
              path: [...basicPaths, "mediaUrls", 0],
            });
          } else if (mediaUrls.length > 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18next.t(FIELD_MAX_LENGTH_SELECT, { value: 10 }),
              path: [...basicPaths, "mediaUrls"],
            });
          } else {
            (data.mediaUrls || []).forEach((mediaUrl, index) => {
              if (!mediaUrl?.trim()) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_REQUIRED),
                  path: [...basicPaths, "mediaUrls", index],
                });
              }
            });
          }

          if (shouldValidateTitles) {
            const titles = data?.titles || [];
            titles.forEach((title, index) => {
              if (
                title?.value?.length >
                ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "titles", index, "value"],
                });
              }
            });
          }

          if (shouldValidateHeadlinesAndDescriptions) {
            const headlines = data?.headlines || [];
            headlines.forEach((headline, index) => {
              if (
                headline?.value?.length >
                ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "headlines", index, "value"],
                });
              }
            });

            const descriptions = data?.descriptions || [];
            descriptions.forEach((description, index) => {
              if (
                description?.value?.length >
                ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "descriptions", index, "value"],
                });
              }
            });
          }
        } else if (isCarousel) {
          const mediaData = data.mediaData || [];
          const mediaUrls = data.mediaUrls || [];

          if (hasCatalogCarouselCard(mediaData)) {
            addCatalogCarouselIntroMediaUrlIssueIfMissing(
              data,
              ctx,
              basicPaths
            );
          } else {
            if (!mediaUrls?.length) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18next.t(FIELD_REQUIRED),
                path: [...basicPaths, "mediaUrls", 0],
              });
            }

            mediaUrls.forEach((mediaUrl, index) => {
              if (!mediaUrl?.trim()) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_REQUIRED),
                  path: [...basicPaths, "mediaUrls", index],
                });
              }
            });
          }

          if (shouldValidateTitles) {
            data.titles.forEach((title, index) => {
              if (
                title?.value?.length >
                ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "titles", index, "value"],
                });
              }
            });
          }

          if (shouldValidateHeadlinesAndDescriptions) {
            const expectedLength = mediaUrls.length;

            const headlines = data.headlines?.slice(0, expectedLength) || [];
            const descriptions =
              data.descriptions?.slice(0, expectedLength) || [];

            headlines.forEach((headline, index) => {
              if (
                headline?.value?.length >
                ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "headlines", index, "value"],
                });
              }
            });

            descriptions.forEach((description, index) => {
              if (
                description?.value?.length >
                ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "descriptions", index, "value"],
                });
              }
            });
          }
        } else {
          if (!isPostAdMode) {
            const mediaUrls = (data?.mediaUrls || []).filter((mediaUrl) =>
              mediaUrl?.trim()
            );
            if (!mediaUrls?.length) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18next.t(FIELD_REQUIRED),
                path: [...basicPaths, "mediaUrls", 0],
              });
            }
          }

          if (shouldValidateTitles) {
            const titles = data?.titles || [];
            titles.forEach((title, index) => {
              if (
                title?.value?.length >
                ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_TITLE_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "titles", index, "value"],
                });
              }
            });
          }

          if (shouldValidateHeadlinesAndDescriptions) {
            const headlines = data?.headlines || [];
            headlines.forEach((headline, index) => {
              if (
                headline?.value?.length >
                ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_HEADLINE_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "headlines", index, "value"],
                });
              }
            });

            const descriptions = data?.descriptions || [];
            descriptions.forEach((description, index) => {
              if (
                description?.value?.length >
                ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[META]
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: i18next.t(FIELD_MAX_LENGTH, {
                    value: ADS_CAMPAIGN_PLATFORM_DESCRIPTION_MAX_LENGTH[META],
                  }),
                  path: [...basicPaths, "descriptions", index, "value"],
                });
              }
            });
          }
        }
      }

      // CATALOG VALIDATIONS
      if (isCatalogAd) {
        if (!data.productSetID) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18next.t(FIELD_REQUIRED),
            path: [...basicPaths, "productSetID"],
          });
        }

        if (isCarousel) {
          addCatalogCarouselIntroMediaUrlIssueIfMissing(data, ctx, basicPaths);
        }
        if (isInstantExperience) {
          if (_.isEmpty(data?.instantExperience?.link)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18next.t(FIELD_REQUIRED),
              path: [...basicPaths, "instantExperience"],
            });
          }
          if (_.isEmpty(data?.headlines?.[0]?.value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18next.t(FIELD_REQUIRED),
              path: [...basicPaths, "headlines", 0, "value"],
            });
          }
        }
      }
    });
